import { useCallback } from 'react'
import { InlineEditField } from '@/components/InlineEditField'
import { businessService } from '@/services/businessService'
import { validateEmployeeCount, validateYearOfFounding, validateLocationCount } from '@/utils/validators'
import { useToast } from '@/components/ui/MobileToast'

interface OperationalDataTabProps {
  business: any
  onUpdate: (updated: any) => void
}

export function OperationalDataTab({ business, onUpdate }: OperationalDataTabProps) {
  const { showToast } = useToast()

  const handleSave = useCallback(
    async (fieldName: string, value: string | number) => {
      try {
        const updated = await businessService.updateBusiness(business.id, {
          [fieldName]: Number(value),
        })
        onUpdate(updated)

        // Check for badge changes in response
        if ((updated as any).badge_awarded) {
          showToast({
            type: 'success',
            title: '🏆 Badge Earned!',
            message: `Congratulations! You earned the "${(updated as any).badge_awarded}" badge.`,
          })
        }
      } catch (err: any) {
        const message =
          err?.response?.data?.error || err?.message || 'Save failed — please try again'
        throw new Error(message)
      }
    },
    [business.id, onUpdate, showToast]
  )

  const employeeCountValidator = useCallback((value: string): string | null => {
    if (!value && value !== '0') return null
    const numValue = Number(value)
    const result = validateEmployeeCount(numValue)
    return result.valid ? null : (result.error ?? null)
  }, [])

  const yearOfFoundingValidator = useCallback((value: string): string | null => {
    if (!value) return null
    const numValue = Number(value)
    const result = validateYearOfFounding(numValue)
    return result.valid ? null : (result.error ?? null)
  }, [])

  const locationCountValidator = useCallback((value: string): string | null => {
    if (!value) return null
    const numValue = Number(value)
    const result = validateLocationCount(numValue)
    return result.valid ? null : (result.error ?? null)
  }, [])

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Operational Data
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <InlineEditField
            label="Employee Count"
            value={business.employee_count}
            fieldName="employee_count"
            type="number"
            validate={employeeCountValidator}
            onSave={handleSave}
            placeholder="0"
          />

          <InlineEditField
            label="Year of Founding"
            value={business.year_of_founding}
            fieldName="year_of_founding"
            type="number"
            validate={yearOfFoundingValidator}
            onSave={handleSave}
            placeholder="e.g., 2010"
          />

          <InlineEditField
            label="Number of Locations"
            value={business.number_of_locations}
            fieldName="number_of_locations"
            type="number"
            validate={locationCountValidator}
            onSave={handleSave}
            placeholder="1"
          />
        </div>
      </div>
    </div>
  )
}
